/**
 * Utility Functions
 * -----------------
 * Collection of helper functions for account and marketing-related logic.
 * This file may contain multiple utilities used across the application.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */


import { AppConfig } from '@/lib/config/'
import { MarketingHttpClient, NotificationHttpClient } from '../repositories'
import { CLUSTER_MAP, NOTIFICATION_CLUSTER_MAP } from './constants'

// --- Types ---
export interface Account {
  id: number | string
  cluster: string
  [key: string]: unknown
}

/**
 * Verifies account cluster and sets Marketing API base URL.
 * Updates global AppConfig and MarketingHttpClient accordingly.
 *
 * @param account - Account object with cluster info
 */
export function verifyAccountCluster(account: Account): void {
  const { cluster } = account

  // Resolve base URL from map or fallback to generic pattern
  const baseUrl =
    CLUSTER_MAP[cluster] ?? `https://${cluster}-web.maropost.com`

  // Update global config
  AppConfig.MARKETING_BASE_URL = baseUrl

  // Update Axios client base URL
  MarketingHttpClient.defaults.baseURL = `${baseUrl}/v2`
  
  /**
   * Notification Http Client base URL
   */
  const baseUrlNotification = NOTIFICATION_CLUSTER_MAP[cluster?.toLowerCase()] ?? AppConfig.NOTIFICATION_BASE_URL
  NotificationHttpClient.defaults.baseURL = `${baseUrlNotification}/api/v1`
}

/**
 * Utility: Debounce function calls
 * --------------------------------
 * Ensures that a function (`func`) is only executed
 * after a specified delay since the last call.
 *
 * @param func - The function to debounce
 * @param delay - Delay in milliseconds
 * @returns A debounced version of the function
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let debounceTimer: ReturnType<typeof setTimeout>

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => func.apply(this, args), delay)
  }
}

/**
 * Convert GMT header date → EST date string (YYYY-MM-DD)
 */
export function getESTDateFromGMT(gmtString: string): string {
  if (!gmtString) return "";

  const date = new Date(gmtString);

  // Convert to EST (America/New_York)
  const estDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

  return estDate;
}